/**
 * Pencil Import Slice
 * Handles importing graphics from Pencil (.pen files)
 *
 * Imports are additive: the imported file is appended to the current scene.
 * Only one file can be imported at a time.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { t } from "i18next"
import JSONfn from "json-fn"
import type {
    AnySceneObjectT,
    CanvasI,
    PencilImportOptions,
    PencilImportResult,
    PencilImportWarning,
    TimelineI,
} from "tweenly"
import {
    canvasMismatchWarning,
    convertPencilToTweenly,
    parsePencilFile,
    prepareObjectsForScene,
} from "tweenly"
import { injectGoogleFonts } from "../../helpers/googleFonts.helpers"
import { collectReferencedFonts, findMissingFonts } from "../../helpers/pencil/fonts.helpers"
import {
    detectMissingImageReferences,
    MissingImage,
    uploadAndReplaceImages,
    UploadedAsset,
} from "../../helpers/pencil/upload.helpers"
import { extractZipContents } from "../../helpers/pencil/zipProcessor.helpers"
import { selectUndoable } from "../../helpers/selector.helpers"
import { AppThunkT } from "../store"
import { createAssetAsync, CreateAssetT } from "./assets.slice"
import { addImportedFontsToPreferencesAsync, setPreferencesFulfilled } from "./editor.slice"
import { updateMasterTimeline } from "./masterTimeline.slice"
import { addMultiObjectsAction, selectObjects } from "./objects.slice"
import { updTimelineAction, uplTimelineAction } from "./timeline.slice"

export type PencilImportStateT = {
    isImporting: boolean
    lastImportResult: PencilImportResult | null
    error: string | null
}

const initialState: PencilImportStateT = {
    isImporting: false,
    lastImportResult: null,
    error: null,
}

const pencilImportSlice = createSlice({
    name: "pencilImport",
    initialState,
    reducers: {
        setImporting: (state, action: PayloadAction<boolean>) => {
            state.isImporting = action.payload
        },
        setImportResult: (state, action: PayloadAction<PencilImportResult>) => {
            state.lastImportResult = action.payload
        },
        setImportError: (state, action: PayloadAction<string>) => {
            state.error = action.payload
        },
        clearImportResult: (state) => {
            state.lastImportResult = null
            state.error = null
        },
    },
})

export const { setImporting, setImportResult, setImportError, clearImportResult } =
    pencilImportSlice.actions

/**
 * Register images uploaded during an import so they show up in the graphic assets.
 * Returns a warning for every asset that could not be registered - the image itself is
 * already uploaded, so the import continues and the user is informed in the import result.
 */
const registerUploadedAssets =
    (
        uploadedAssets: UploadedAsset[],
        companyId: string
    ): AppThunkT<Promise<PencilImportWarning[]>> =>
    async (dispatch) => {
        const warnings: PencilImportWarning[] = []

        for (const asset of uploadedAssets) {
            const assetPayload: CreateAssetT = {
                name: asset.name,
                slug: asset.slug,
                companyId,
                attributes: [],
                url: asset.url,
                type: asset.type,
                size: asset.size,
                description: "",
            }
            const createdAsset = await dispatch(createAssetAsync(assetPayload, companyId))

            if (!createdAsset) {
                console.warn("[Import] Failed to register asset:", asset.name)
                warnings.push({
                    type: "missing_asset",
                    message: t("editor:importDialog.messages.assetNotRegistered", {
                        name: asset.name,
                    }),
                })
            }
        }

        return warnings
    }

/**
 * Import a Pencil (.pen) file and append its objects to the current scene
 */
export const importPencilFileAction =
    (
        fileContent: string,
        options?: PencilImportOptions,
        imageBlobs?: Map<string, Blob>
    ): AppThunkT<Promise<void>> =>
    async (dispatch, getState) => {
        // Capture graphic context at the start to prevent race conditions
        // if user switches graphics during the async import
        const graphic = getState().graphic.data
        const graphicId = graphic._id
        const companyId = graphic.companyId

        // Parse the .pen file
        const document = parsePencilFile(fileContent)

        // Convert to Tweenly format
        const result = convertPencilToTweenly(document, {
            inlineVariables: true,
            titlePrefix: "[Pencil] ",
            ...options,
        })

        // Load any non-standard fonts from Google Fonts CDN so the editor preview renders correctly
        // Add them to user preferences so they appear in the font dropdown
        if (result.googleFonts.length > 0) {
            injectGoogleFonts(result.googleFonts)
            console.log("[PencilImport] Loading Google Fonts:", result.googleFonts)

            // Add imported fonts to preferences (and persist) so they appear in the font selector
            dispatch(addImportedFontsToPreferencesAsync(result.googleFonts))
        }

        // Make the converted objects conflict-free against what is already on the canvas.
        // Must happen before the image upload - the S3 key is derived from the object ID.
        const existingObjects = selectObjects(selectUndoable(getState()).objects)
        const mergedObjects = prepareObjectsForScene(result.objects, existingObjects)
        const warnings: PencilImportWarning[] = [...result.warnings]

        // The canvas size is only taken over when the scene is still empty
        if (result.canvas) {
            if (existingObjects.length === 0) {
                dispatch(
                    setPreferencesFulfilled({
                        canvasResolution: [result.canvas.width, result.canvas.height],
                    })
                )
            } else {
                const warning = canvasMismatchWarning(
                    result.canvas,
                    getState().editor.value.settings.canvasResolution
                )
                if (warning) warnings.push(warning)
            }
        }

        // Upload images from ZIP and replace relative URLs with S3 public URLs
        let finalObjects = mergedObjects
        if (imageBlobs && imageBlobs.size > 0) {
            console.log("[PencilImport] ZIP image blobs:", Array.from(imageBlobs.keys()))
            console.log("[PencilImport] Image refs from .pen:", result.imageRefs)
        }
        if (imageBlobs && imageBlobs.size > 0 && result.imageRefs.length > 0) {
            if (graphicId && companyId) {
                const uploadResult = await uploadAndReplaceImages(
                    mergedObjects,
                    imageBlobs,
                    graphicId
                )
                finalObjects = uploadResult.objects

                const assetWarnings = await dispatch(
                    registerUploadedAssets(uploadResult.uploadedAssets, companyId)
                )
                warnings.push(...assetWarnings)
            } else {
                console.warn(
                    "[PencilImport] Cannot upload images: graphic ID or company ID not available"
                )
            }
        } else if (result.imageRefs.length > 0 && (!imageBlobs || imageBlobs.size === 0)) {
            console.warn(
                "[PencilImport] Import references images but no image files provided:",
                result.imageRefs
            )
        }

        // Store the result for inspection (after image URL replacement to avoid Immer freeze)
        dispatch(setImportResult({ ...result, objects: finalObjects, warnings }))

        // Append objects to the scene
        if (finalObjects.length > 0) {
            await dispatch(addMultiObjectsAction(finalObjects))

            // Rebuild the master timeline so the appended objects are animated too
            dispatch(updateMasterTimeline(0))

            // Show success message with stats
            console.log("[PencilImport] Import completed:", {
                converted: result.stats.convertedNodes,
                total: result.stats.totalNodes,
                imported: finalObjects.length,
                warnings: warnings.length,
                errors: result.errors.length,
            })

            // Log warnings and errors
            if (warnings.length > 0) {
                console.warn("[PencilImport] Warnings:", warnings)
            }
            if (result.errors.length > 0) {
                console.error("[PencilImport] Errors:", result.errors)
            }
        }
    }

/**
 * Import a Tweenly scene.json file exported from the ZIP package.
 * Appends objects (with embedded animations) to the current scene.
 */
export const importTweenlyJsonAction =
    (
        fileContent: string,
        imageBlobs?: Map<string, Blob>,
        fontBlobs?: Map<string, Blob>
    ): AppThunkT<Promise<void>> =>
    async (dispatch, getState) => {
        // Capture graphic context at the start to prevent race conditions
        // if user switches graphics during the async import
        const graphic = getState().graphic.data
        const graphicId = graphic._id
        const companyId = graphic.companyId

        const data = JSONfn.parse(fileContent) as {
            _tweenly_export_version: string
            canvas: CanvasI
            objects: AnySceneObjectT[]
            timeline: TimelineI
        }

        if (!data.objects || !Array.isArray(data.objects)) {
            throw new Error("Invalid scene.json: missing objects array")
        }

        // Validate canvas dimensions
        if (
            data.canvas &&
            (typeof data.canvas.width !== "number" || typeof data.canvas.height !== "number")
        ) {
            throw new Error("Invalid scene.json: canvas missing width/height")
        }

        // Validate each object has required fields
        for (const obj of data.objects) {
            if (!obj.id || !obj.type) {
                throw new Error(
                    `Invalid scene.json: object missing required fields (id: ${obj.id}, type: ${obj.type})`
                )
            }
        }

        const warnings: PencilImportWarning[] = []

        // Make the imported objects conflict-free against what is already on the canvas.
        // Scene.json keeps its original IDs, so re-importing the same file would collide.
        // Must happen before the image upload - the S3 key is derived from the object ID.
        const existingObjects = selectObjects(selectUndoable(getState()).objects)
        const isEmptyScene = existingObjects.length === 0
        const mergedObjects = prepareObjectsForScene(data.objects, existingObjects)

        // The canvas size is only taken over when the scene is still empty
        if (data.canvas) {
            if (isEmptyScene) {
                dispatch(
                    setPreferencesFulfilled({
                        canvasResolution: [data.canvas.width, data.canvas.height],
                    })
                )
            } else {
                const warning = canvasMismatchWarning(
                    data.canvas,
                    getState().editor.value.settings.canvasResolution
                )
                if (warning) warnings.push(warning)
            }
        }

        // --- Detect missing fonts ---
        const referencedFonts = collectReferencedFonts(mergedObjects)
        const missingFonts = findMissingFonts(referencedFonts, fontBlobs ?? new Map())
        for (const font of missingFonts) {
            warnings.push({
                type: "missing_asset",
                message: t("editor:importDialog.messages.missingFont", { name: font }),
            })
        }

        // Try loading missing fonts from Google Fonts CDN as fallback
        // Add them to user preferences so they appear in the font dropdown
        const googleFonts = missingFonts.length > 0 ? missingFonts : []
        if (googleFonts.length > 0) {
            injectGoogleFonts(googleFonts)

            // Add imported fonts to preferences (and persist) so they appear in the font selector
            dispatch(addImportedFontsToPreferencesAsync(googleFonts))
        }

        // --- Re-upload images and detect missing ones ---
        let finalObjects = mergedObjects
        let missingImages: MissingImage[] = []

        if (imageBlobs && imageBlobs.size > 0) {
            if (graphicId && companyId) {
                const uploadResult = await uploadAndReplaceImages(
                    mergedObjects,
                    imageBlobs,
                    graphicId
                )
                finalObjects = uploadResult.objects
                missingImages = uploadResult.missingImages

                const assetWarnings = await dispatch(
                    registerUploadedAssets(uploadResult.uploadedAssets, companyId)
                )
                warnings.push(...assetWarnings)
            }
        } else {
            // No image blobs at all — check if any objects reference local images
            missingImages = detectMissingImageReferences(mergedObjects)
        }

        for (const img of missingImages) {
            warnings.push({
                type: "missing_asset",
                nodeId: img.objectId,
                nodeName: img.objectTitle,
                message: t("editor:importDialog.messages.missingImage", {
                    name: img.imageRef,
                    objectTitle: img.objectTitle,
                }),
            })
        }

        // Append objects to scene (animations are embedded in each object)
        await dispatch(addMultiObjectsAction(finalObjects))

        // Restore timeline if present. An existing scene keeps its own timeline settings and
        // labels - only the duration is stretched so the appended animations fit in.
        if (data.timeline) {
            if (isEmptyScene) {
                dispatch(uplTimelineAction({ timeline: data.timeline }))
            } else {
                const minduration = Math.max(
                    getState().timeline.value.minduration,
                    data.timeline.minduration
                )
                dispatch(updTimelineAction({ property: "minduration", value: minduration }))
                warnings.push({
                    type: "partial_support",
                    message: t("editor:importDialog.messages.timelineSkipped"),
                })
            }
        }

        // Initialize master GSAP timeline with imported animations
        dispatch(updateMasterTimeline(0))

        const syntheticResult: PencilImportResult = {
            objects: finalObjects,
            canvas: data.canvas ?? null,
            warnings,
            errors: [],
            skippedNodes: [],
            googleFonts,
            imageRefs: missingImages.map((m) => m.imageRef),
            stats: {
                totalNodes: data.objects.length,
                convertedNodes: data.objects.length,
                skippedNodes: 0,
                reusableComponents: 0,
            },
        }
        dispatch(setImportResult(syntheticResult))

        console.log("[TweenlyImport] Import completed:", {
            objects: data.objects.length,
            version: data._tweenly_export_version,
            warnings: warnings.length,
        })
    }

/**
 * Import the file picked in the import dialog (.pen, .tw/.json or a .zip containing either)
 * and append its content to the current scene.
 */
export const importFileAction =
    (file: File): AppThunkT<Promise<void>> =>
    async (dispatch) => {
        dispatch(setImporting(true))

        try {
            if (file.name.endsWith(".zip")) {
                // Extract ZIP: find .pen or .tw file and images
                const contents = await extractZipContents(file)

                if (contents.type === "none") {
                    throw new Error(t("editor:importDialog.messages.noSceneInZip"))
                }

                // Prefer .tw over .pen (Tweenly native format preserves more data)
                await dispatch(
                    contents.type === "tweenly"
                        ? importTweenlyJsonAction(
                              contents.content,
                              contents.imageBlobs,
                              contents.fontBlobs
                          )
                        : importPencilFileAction(contents.content, undefined, contents.imageBlobs)
                )
            } else {
                const fileContent = await file.text()
                const isTweenlyJson = file.name.endsWith(".tw") || file.name.endsWith(".json")

                await dispatch(
                    isTweenlyJson
                        ? importTweenlyJsonAction(fileContent)
                        : importPencilFileAction(fileContent)
                )
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : t("editor:importDialog.messages.unknownError")
            console.error(`[Import] Failed to import ${file.name}:`, error)
            dispatch(setImportError(`${file.name}: ${errorMessage}`))
        } finally {
            dispatch(setImporting(false))
        }
    }

export default pencilImportSlice.reducer
